import React, {MouseEventHandler, useEffect, useState} from 'react';
import intl from "react-intl-universal";
import classNames from "classnames";


import {hotkeys, HOTKEYS_SCOPE, HotkeysScopeValue, setScope} from "../../../utils/hotkeys";
import {Button, ButtonType} from "../Button";
import {ModalBase, ModalConfirmType, ModalBaseProps} from "../../screens/modals/components/ModalBase";


import {screenSizeProvider} from "../../../stores/ScreenSizeProvider";


import './ModalConfirm.css';


export interface ModalConfirmProps extends ModalBaseProps {
    onConfirm: () => void | Promise<void>;
    confirmText?: string;
    onCancel?: () => void;
    cancelText?: string;
    onClick?: MouseEventHandler;
    reverseButtons?: boolean;
    canConfirm?: boolean;
}

export const ModalConfirm = (
    {
        open,
        type,
        header,
        children,
        onClose,
        onConfirm,
        canConfirm = true,
        confirmText = intl.get('common.Submit').d('Submit'),
        onCancel,
        cancelText = intl.get('common.Cancel').d('Cancel'),
        onClick,
        maxWidth = 640,
        minWidth = 640,
        reverseButtons = false,
        isMobileFullScreen,
        isMobileBottomSheet,
        isHeaderSticky,
        isDark,
        subtitle,
        maxHeight,
        contentClassName
    }: ModalConfirmProps
) => {
    const [isProcessingConfirm, setIsProcessingConfirm] = useState(false);
    const isMobile = screenSizeProvider.view !== 'desktop';

    useEffect(() => {
        const previousScope = hotkeys.getScope();
        setScope(HOTKEYS_SCOPE.MODAL);
        const handleConfirm = (event: any) => {
            if (open) {
                event.preventDefault();
                onConfirm();
            }
        };
        const handleEsc = (event: any) => {
            if (open && onClose) {
                onClose();
            }
        };
        hotkeys(
            'enter,num_enter',
            {
                scope: HOTKEYS_SCOPE.MODAL,
                keyup: false,
                keydown: true,
            },
            handleConfirm,
        )
        hotkeys(
            'esc',
            {
                scope: HOTKEYS_SCOPE.MODAL,
            },
            handleEsc,
        )

        return () => {
            hotkeys.unbind('enter,num_enter,esc', HOTKEYS_SCOPE.MODAL, handleConfirm);
            hotkeys.unbind('enter,num_enter,esc', HOTKEYS_SCOPE.MODAL, handleEsc);
            setScope(previousScope as HotkeysScopeValue);
        }
    }, [open, onConfirm, onClose]);

    const cancelBtnType = isDark ? 'soft' : 'plain';

    return (
        <ModalBase
            open={open}
            type={type}
            header={header}
            onClose={onClose}
            onClick={onClick}
            maxWidth={maxWidth}
            minWidth={minWidth}
            maxHeight={maxHeight}
            isDark={isDark}
            isMobileFullScreen={isMobileFullScreen}
            isMobileBottomSheet={isMobileBottomSheet}
            isHeaderSticky={isHeaderSticky}
            subtitle={subtitle}
            contentClassName={contentClassName}
        >
            {children}
            <div className={classNames({
                'ModalConfirm_actions': true,
                'ModalConfirm_actions--reverse': reverseButtons,
            })}>
                <Button
                    className={classNames({"FullScreenModalButton": isMobile})}
                    style={reverseButtons ? 'solid' : cancelBtnType}
                    type={reverseButtons ? matchButtonType(type) : 'neutral'}
                    text={cancelText}
                    onClick={onCancel}
                    size={'md'}
                />
                <Button
                    isDisable={!canConfirm}
                    className={classNames({"FullScreenModalButton": isMobile})}
                    style={reverseButtons ? cancelBtnType : 'solid'}
                    type={reverseButtons ? 'neutral' : matchButtonType(type)}
                    text={confirmText}
                    onClick={async () => {
                        if (!canConfirm) return;
                        setIsProcessingConfirm(true);
                        await onConfirm();
                        setIsProcessingConfirm(false);
                    }}
                    size={'md'}
                    loading={isProcessingConfirm}
                />
            </div>
        </ModalBase>
    )
}

const matchButtonType = (type?: ModalConfirmType): ButtonType => {
    switch (type) {
        case 'info':
            return 'primary';
        case 'warning':
            return 'accent';
        case undefined:
            return 'primary';
        default:
            return type;
    }
}

